import { AgentRunAttachment, AssistantAgentSession } from '@superfunnel/types';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAgentChat } from '../../contexts/AgentChatContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { UserContext } from '../../contexts/UserContext';
import { useNavigationBlocker } from '../useNavigationBlocker';
import {
  useAssistantAgentSession,
  UseAssistantAgentSessionReturn,
} from './useAssistantAgentSession';
import {
  useDraftCardActions,
  UseDraftCardActionsReturn,
} from './useDraftCardActions';

/**
 * Editor modal state for opening the variant editor
 */
export interface EditorModalState {
  websiteId: string;
  variantId: string;
  dynamicPageId: string;
  initialMessage?:
    | string
    | { content: string; attachments?: AgentRunAttachment[] };
}

/**
 * Return type for the useAiAssistantLogic hook
 */
export interface UseAiAssistantLogicReturn
  extends UseAssistantAgentSessionReturn,
    UseDraftCardActionsReturn {
  // Chat state
  hasMessages: boolean;
  isWorking: boolean;
  isLoading: boolean;
  isStreaming: boolean;
  lastUpdated: Date | null;
  messages: ReturnType<
    ReturnType<typeof useAgentChat>['getAgentState']
  >['messages'];

  /** The dynamic page ID */
  dynamicPageId: string | undefined;
  /** The dynamic page data (from structureData) */
  dynamicPageData:
    | NonNullable<AssistantAgentSession['structureData']>['dynamicPage']
    | undefined;

  // Chat actions
  sendMessage: ReturnType<typeof useAgentChat>['sendMessage'];
  stopStreaming: ReturnType<typeof useAgentChat>['stopStreaming'];

  // Prompt context
  promptContexts: ReturnType<typeof useAgentChat>['promptContexts'];
  setPromptContexts: ReturnType<typeof useAgentChat>['setPromptContexts'];
  updatePromptContext: ReturnType<typeof useAgentChat>['updatePromptContext'];
  addPromptContext: ReturnType<typeof useAgentChat>['addPromptContext'];
  userMessageHistory: string[];

  // Editor modal
  editorModal: EditorModalState | undefined;
  openEditor: (state: EditorModalState) => void;
  closeEditor: () => void;

  /** Whether draft card actions are disabled */
  isDraftCardDisabled: boolean;

  // New chat action
  resetAgentSession: ReturnType<typeof useAgentChat>['resetAgentSession'];

  // Analytics
  trackEvent: ReturnType<typeof useAnalytics>['track'];
}

/**
 * Composite hook for AI Assistant screen logic
 * Combines:
 * - Agent chat state and actions
 * - Assistant agent session (Firestore)
 * - Tool call subscriptions
 * - Editor modal state
 * - Navigation blocking
 * - Analytics tracking
 *
 * This follows the pattern established by useEditorLogic for the Editor component.
 */
export function useAiAssistantLogic(): UseAiAssistantLogicReturn {
  const { track } = useAnalytics();
  const { selectedAccount } = useContext(UserContext);
  const {
    getAgentState,
    sendMessage,
    stopStreaming,
    subscribeToToolCall,
    promptContexts,
    setPromptContexts,
    updatePromptContext,
    addPromptContext,
    resetAgentSession,
  } = useAgentChat();

  // Get agent state
  const agentState = getAgentState();

  // User message history for input autocomplete
  const userMessageHistory = useMemo(
    () =>
      agentState.messages
        .filter((msg) => msg.type === 'user')
        .map((msg) => msg.content)
        .reverse(),
    [agentState.messages],
  );

  // Assistant agent session from Firestore
  const assistantSession = useAssistantAgentSession();

  const dynamicPageId = useMemo(() => {
    if (!assistantSession) return undefined;
    if (!assistantSession.structureData) return undefined;

    return (
      assistantSession.structureData?.createdDynamicPageId ||
      assistantSession.agentSessionData?.dynamicPageId ||
      'draft-only'
    );
  }, [assistantSession]);

  const dynamicPageData = useMemo(() => {
    if (!assistantSession) return undefined;
    if (!assistantSession.structureData) return undefined;
    return assistantSession.structureData?.dynamicPage;
  }, [assistantSession]);

  // Editor modal state
  const [editorModal, setEditorModal] = useState<EditorModalState | undefined>(
    undefined,
  );

  // Editor modal actions
  const openEditor = useCallback((state: EditorModalState) => {
    setEditorModal(state);
  }, []);

  const closeEditor = useCallback(() => {
    if (editorModal) {
      track('ai_assistant_editor_closed', {
        dynamic_page_id: editorModal.dynamicPageId,
      });
    }
    setEditorModal(undefined);
  }, [editorModal, track]);

  // Draft card actions
  const draftCardActions = useDraftCardActions({
    accountId: selectedAccount?.id,
    assistantSession,
    messages: agentState.messages,
    openEditorModal: openEditor,
  });

  // Derived state
  const hasMessages = agentState.messages.length > 0;
  const isDraftCardDisabled =
    agentState.isLoading ||
    agentState.isStreaming ||
    !!draftCardActions.generatingVariantId;

  // Track screen view on mount
  useEffect(() => {
    track('ai_assistant_screen_viewed');
  }, [track]);

  // Block navigation when there are active messages
  useNavigationBlocker(
    hasMessages,
    'You have an active conversation. Are you sure you want to leave? Your progress will be lost.',
  );

  // Subscribe to tool calls
  useEffect(() => {
    console.log('[useAiAssistantLogic] Setting up tool call listeners');

    // Subscribe to open_variant_editor tool
    const unsubscribeEditor = subscribeToToolCall(
      'open_variant_editor',
      (event) => {
        console.log(
          '[useAiAssistantLogic] 🎯 open_variant_editor tool called:',
          {
            toolName: event.toolName,
            input: event.input,
            output: event.output,
            metadata: event.metadata,
          },
        );

        track('ai_assistant_tool_called', {
          tool_name: 'open_variant_editor',
          success: event.output?.success || false,
        });

        // Extract data from output
        const { dynamicPageId, variantId, websiteId, contentGenerationPrompt } =
          event.output.data;
        console.log('[useAiAssistantLogic] 📝 Editor details:', {
          dynamicPageId,
          variantId,
          websiteId,
        });

        // Check output for success
        if (event.output?.success) {
          console.log('[useAiAssistantLogic] ✅ Tool executed successfully');

          const allAttachments = agentState.messages
            .flatMap((msg) =>
              Array.isArray(msg.attachments) ? msg.attachments : [],
            )
            .filter(
              (att): att is AgentRunAttachment =>
                att !== null && att !== undefined && att.type === 'file',
            );

          track('ai_assistant_editor_opened', {
            dynamic_page_id: dynamicPageId,
            variant_id: variantId,
            website_id: websiteId,
            attachments_count: allAttachments.length,
            starting_generation: event.input.startContentGeneration
              ? true
              : false,
          });

          setEditorModal({
            websiteId,
            variantId,
            dynamicPageId,
            initialMessage: contentGenerationPrompt
              ? {
                  content: contentGenerationPrompt,
                  attachments: allAttachments,
                }
              : undefined,
          });
        } else {
          console.error(
            '[useAiAssistantLogic] ❌ Tool execution failed:',
            event.output?.error,
          );
          track('ai_assistant_tool_failed', {
            tool_name: 'open_variant_editor',
            error: event.output?.error || 'Unknown error',
          });
        }
      },
    );

    return () => {
      console.log('[useAiAssistantLogic] Cleaning up tool call listeners');
      unsubscribeEditor();
    };
  }, [subscribeToToolCall, agentState.messages, track]);

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // Session data from assistant agent session
      ...assistantSession,

      // Chat state
      hasMessages,
      isWorking: agentState.isWorking,
      isLoading: agentState.isLoading,
      isStreaming: agentState.isStreaming,
      lastUpdated: agentState.lastUpdated,
      messages: agentState.messages,

      // Chat actions
      sendMessage,
      stopStreaming,

      // Prompt context
      promptContexts,
      setPromptContexts,
      updatePromptContext,
      addPromptContext,
      userMessageHistory,

      // Editor modal
      editorModal,
      openEditor,
      closeEditor,

      // DynamicPageDraftCard actions
      ...draftCardActions,
      isDraftCardDisabled,

      // Dynamic page data for settings modal
      dynamicPageId,
      dynamicPageData,

      // New chat action
      resetAgentSession,

      // Analytics
      trackEvent: track,
    }),
    [
      assistantSession,
      hasMessages,
      agentState.isWorking,
      agentState.isLoading,
      agentState.isStreaming,
      agentState.lastUpdated,
      agentState.messages,
      sendMessage,
      stopStreaming,
      promptContexts,
      setPromptContexts,
      updatePromptContext,
      addPromptContext,
      userMessageHistory,
      editorModal,
      openEditor,
      closeEditor,
      draftCardActions,
      isDraftCardDisabled,
      dynamicPageId,
      dynamicPageData,
      resetAgentSession,
      track,
    ],
  );
}
